import {
  Briefcase,
  ClipboardList,
  Inbox,
  Key,
  Search,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessDecisionQueue } from "@/hooks/useAccessDecisionQueue";
import { useResponsibleStepCount } from "@/hooks/useResponsibleStepCount";

/**
 * Where a member can go from the top of the app — **one list, read by both
 * surfaces that offer it** (Jira NAV-90).
 *
 * The top nav and the dashboard's action cards were two hand-written lists of
 * the same destinations, so they drifted: the dashboard offered a subset, and
 * the evaluation queue existed only in the nav bar. A member who navigates from
 * the dashboard — which is where they land — could not reach their own steps at
 * all. Two lists of one thing will drift again, so there is one, and each
 * surface renders it its own way.
 *
 * The avatar menu reads one entry off it too (`UserDropdown`), for the same
 * reason: that menu is the only way to these pages from an admin shell, which
 * suppresses the navigation bar, and it was counting the work itself.
 *
 * The two icons per entry are deliberate: the nav bar wants a small mark beside
 * a short label, the dashboard card wants the picture of a place. Same
 * destination, different room for it.
 */
export interface EndUserDestination {
  /** Suffix of both surfaces' test ids (`nav-…`, `action-card-…`). */
  key: string;
  to: string;
  /**
   * Search params to open the destination with, when a bare address would not
   * land where the member needs to be.
   *
   * One entry uses it: "Waiting on you" holds two tabs, and a bare address opens
   * the steps (Jira NAV-111). The entry points at whichever tab has work, so
   * arriving from the bar or the dashboard lands on something to do rather than
   * on an empty state — and the *page* keeps a fixed default instead of
   * following the counts, which would move a reader to the other tab the moment
   * they finished their last step.
   */
  search?: Record<string, string>;
  /** In the `navigation` bundle — one name per destination, everywhere. */
  labelKey: string;
  /** In the `endUser` bundle: the card's second line. The nav has no room. */
  descriptionKey: string;
  navIcon: LucideIcon;
  cardIcon: LucideIcon;
  /** Card icon tile, which is the dashboard's way of telling them apart. */
  iconBg: string;
  iconColor: string;
}

/** A destination with whatever is waiting on this member counted onto it. */
export interface OfferedDestination extends EndUserDestination {
  /**
   * How much is waiting, when something is. Undefined when nothing is — which
   * is not the same as the entry being absent: it is offered for a page worth
   * opening and badged for work to do, and those part company (see
   * {@link useEndUserDestinations}). Never 0: one of the three surfaces draws
   * the pill on `!== undefined`, and a badge reading "0" is a claim.
   */
  badge?: number;
}

const MY_TOOLS = {
  key: "my-tools",
  to: "/end-user/my-tools",
  labelKey: "nav.yourApprovedTools",
  descriptionKey: "dashboard.viewApprovedDesc",
  navIcon: Briefcase,
  cardIcon: ClipboardList,
  iconBg: "bg-blue-50",
  iconColor: "text-blue-500",
} as const satisfies EndUserDestination;

const DISCOVER = {
  key: "tools",
  to: "/end-user/tools",
  labelKey: "nav.discoverNew",
  descriptionKey: "dashboard.discoverDesc",
  navIcon: Search,
  cardIcon: Sparkles,
  iconBg: "bg-amber-50",
  iconColor: "text-amber-500",
} as const satisfies EndUserDestination;

const REQUEST_ACCESS = {
  key: "request-access",
  to: "/end-user/request-access",
  labelKey: "nav.requestAccess",
  descriptionKey: "dashboard.requestAccessDesc",
  navIcon: ClipboardList,
  cardIcon: Key,
  iconBg: "bg-orange-50",
  iconColor: "text-orange-500",
} as const satisfies EndUserDestination;

const WAITING_ON_YOU = {
  key: "waiting-on-you",
  to: "/end-user/waiting-on-you",
  labelKey: "nav.waitingOnYou",
  descriptionKey: "dashboard.waitingOnYouDesc",
  navIcon: Inbox,
  cardIcon: Inbox,
  iconBg: "bg-rose-50",
  iconColor: "text-rose-500",
} as const satisfies EndUserDestination;

/**
 * What to offer this member, in order, with the counts on it.
 *
 * Three destinations are permanent. **"Waiting on you" is not.** It holds the
 * two lists that used to be two entries — the evaluation steps somebody is
 * responsible for, and the access requests they have to settle (Jira NAV-111) —
 * and it is offered exactly when either half has a page worth opening. Most
 * members are never an approver, an activator or a step responsible, so a
 * permanent entry would be a destination nearly everybody has no use for.
 *
 * **Offered on content, badged on work, and those are not the same set.** This
 * list is the *only* route to the page — the bar, the dashboard cards and the
 * avatar menu all read it, and nothing else links there — so a gate that asked
 * "is anything waiting?" locked people out of a page that still had their work
 * on it and still took input (reviewed on PR #100). The step count is narrower
 * than the step list by design: it drops a decided request, because a settled
 * request must stop nagging even though HBV's steps stay tickable past the
 * decision, and it drops a step whose dependency is still open, because a badge
 * that counts it asks for work nobody can do yet. Both of those are on the page.
 * So the entry is offered when either half has *content* — the badge is what
 * goes to zero. The decisions half needs no equivalent: its tab renders exactly
 * the queue its count comes from.
 *
 * **One badge over both halves, counting each piece of work once.** The two
 * counts are in different units — steps on one side (one request can hold
 * several of yours) and requests on the other — so they cannot simply be added:
 * a request that is both the reader's to settle *and* carries a step of theirs
 * would be counted twice. The steps side reports which requests its number is
 * made of, and a decision on one of those adds nothing further. That case
 * cannot arise from the current model — an evaluation carries steps and no
 * route, an access request carries a route and no steps — so this is a guard
 * against a shape nothing produces yet, written because NAV-111 asked for it by
 * name; and it is a *request*-level dedup, so if such a request ever existed its
 * decision would fold into its steps rather than adding a unit of its own.
 *
 * **The two halves belong to different people while impersonating, so the badge
 * counts one of them.** Every "my …" surface follows `actingUid`, and the step
 * count does too — but a decision is a *write*, and `firestore.rules` matches
 * `request.auth.token.email`, which is always the admin's own, so the decision
 * queue is deliberately keyed on the real signed-in address
 * (`useAccessDecisionQueue`). Summed, that is a number belonging to nobody: the
 * impersonated person's steps plus the admin's own decisions. So while viewing
 * as someone else the badge counts the steps alone, which is whose world the
 * screen is showing — and nothing is hidden by that, because impersonation
 * refuses writes outright (`useImpersonationGuard`) and settling a decision is
 * nothing but a write.
 *
 * The count matters more than it looks: without it the entry reads as just
 * another destination, and a responsible would learn a step is theirs only from
 * the D7 reminder email — the slow path, by design.
 *
 * **Nothing is offered until both halves have answered**, so the entry appears a
 * moment after the bar does rather than with it. Unavoidable in part — nothing
 * knows whether work is waiting until the queries answer, and the alternative,
 * a permanent entry, is the destination-nobody-needs this gate exists to avoid.
 * But it is also deliberate, because an unsettled count decides more than
 * whether a badge is drawn: a zero that means "not yet" would point the entry at
 * the wrong tab, and the reader clicking in that window would land on the empty
 * one and watch the target move behind them (reviewed on PR #100). The steps
 * count is the slow half — a query for the requests, then a listener each — and
 * `useResponsibleStepCount` reports itself loading until all of them are in.
 */
export function useEndUserDestinations(): OfferedDestination[] {
  const { isImpersonating } = useAuth();
  const {
    count: assignedStepCount,
    requestIds: steppedRequestIds,
    listedRequestCount,
    loading: stepsLoading,
    error: stepsError,
  } = useResponsibleStepCount();
  const {
    mine: decisionsWithYou,
    loading: decisionsLoading,
    error: decisionsError,
  } = useAccessDecisionQueue();

  const countedOnSteps = new Set(steppedRequestIds);
  const decisions = isImpersonating
    ? []
    : decisionsWithYou.filter(
        (queued) => !countedOnSteps.has(queued.request.id),
      );
  const waiting = assignedStepCount + decisions.length;
  // The decision queue is not waited on while impersonating, because its
  // result is discarded there — see the paragraph above on whose work each half
  // is. The listeners themselves run: that effect is keyed on the address, which
  // impersonation does not change.
  const settled = !stepsLoading && (isImpersonating || !decisionsLoading);
  const readFailed = Boolean(stepsError || (!isImpersonating && decisionsError));
  // Each half's own "has this answered" — a refused read has answered, with a
  // zero that means nothing. Kept apart from `settled` because the tab hint
  // below needs them separately: one half failing must not suppress a pointer
  // the *other* half can still justify.
  const stepsAnswered = !stepsLoading || Boolean(stepsError);
  const decisionsAnswered = !isImpersonating && !decisionsLoading;

  return [
    MY_TOOLS,
    DISCOVER,
    REQUEST_ACCESS,
    // **A read that failed still offers the destination.** Every number here is
    // a count, and a refused query leaves all of them at zero with `settled`
    // true — which this gate reads as "no work is waiting on you" and omits the
    // entry. That removes the only route to the queue from somebody who has
    // work in it, and says nothing: the badge is derived from the same zeros,
    // so there is no other signal on screen. Offered without a badge instead,
    // and the page it opens now says the read failed rather than showing an
    // empty queue. The decision half's result is discarded while
    // impersonating (see above), so its error cannot count there.
    ...(readFailed || (settled && (waiting > 0 || listedRequestCount > 0))
      ? [
          {
            ...WAITING_ON_YOU,
            badge: !readFailed && waiting > 0 ? waiting : undefined,
            // Point at the half that has work. The steps tab is what a bare
            // address opens, so it is the one the entry never has to name —
            // including when the entry is offered for content alone, since that
            // content is on the steps tab.
            // **Each half answers for itself.** `decisions.length > 0` is only
            // ever true of a decision read that succeeded — that hook
            // substitutes an empty list on failure — so a non-empty one is
            // known work, and the steps read failing is no reason to stop
            // pointing at it. Suppressing this on any failure was worse than
            // the flip it was guarding against: the tab triggers carry no
            // counts, so a refused steps read left N real decisions with no
            // signal anywhere and the reader landed on an error card instead.
            //
            // Both halves still have to have *answered*, or the count is a zero
            // from a read in flight and the target moves under a reader who is
            // already clicking. A refused read counts as answered: its zero is
            // not going to change.
            search:
              decisionsAnswered &&
              stepsAnswered &&
              assignedStepCount === 0 &&
              decisions.length > 0
                ? { tab: "decisions" }
                : undefined,
          },
        ]
      : []),
  ];
}
